import type { TopLevelSpec } from "vega-lite";
import type { ChartData } from "../types.js";

type TitleValue = TopLevelSpec["title"];

/** Builds a Vega-Lite title from an optional title + subtitle pair. */
export function buildTitle(
  title?: string,
  subtitle?: string,
): TitleValue | undefined {
  if (subtitle) return { text: title ?? "", subtitle };
  return title;
}

/** Wraps tabular data as a mutable inline Vega dataset. */
export function inlineData(data: ChartData): {
  values: Record<string, unknown>[];
} {
  return { values: [...data] };
}

/**
 * A horizontal category axis whose labels never run into each other: each
 * is cut (with an ellipsis) to the room its category has across the plot,
 * and labels that would still overlap are skipped, so thousands of
 * categories stay legible. The tooltip names a category in full.
 */
export function categoryAxisLabels(
  data: ChartData,
  field: string,
): { labelLimit: { expr: string }; labelOverlap: true } {
  const count = Math.max(1, new Set(data.map((row) => row[field])).size);
  return {
    labelLimit: { expr: `max(24, width / ${count} - 6)` },
    labelOverlap: true,
  };
}

/**
 * A column name as a Vega-Lite `field`: a `.` or `[` in a field is a nested
 * access, so a column named "Net.Amount" must have them escaped to be read
 * as itself.
 */
export function fieldRef(name: string): string {
  return name.replace(/[.[\]]/g, (char) => `\\${char}`);
}
